/**
 * BOGO (1+1 free) post-ATC popup (snippets/post-atc-bogo-popup.liquid).
 *
 * Shows the offer picked in the switcher ([data-bogo-switch]) and keeps the popup in sync with the
 * selected shades: prices, the selected-value labels and the accept button. The accept button gets
 * [data-atc-popup-items] with the paid line (_discountMixedBOGOGeneral) and the free line (_discountMixedBOGO),
 * linked by _mixedBOGOPair, plus the offer's "added to cart" message in [data-atc-popup-message].
 * assets/combined-listing-popup.js adds those items to the cart when the chain ends.
 */
if (!customElements.get('post-atc-bogo')) {
  customElements.define(
    'post-atc-bogo',
    class PostAtcBogo extends HTMLElement {
      connectedCallback() {
        // <modal-dialog> moves itself to <body>, which reconnects this element.
        if (this.initialized) return;
        this.initialized = true;

        this.acceptButton = this.querySelector('[data-atc-popup-accept]');
        this.addEventListener('change', this.onChange.bind(this));

        const switchInput = this.querySelector('[data-bogo-switch]:checked');
        this.showOffer(switchInput ? switchInput.value : this.querySelector('.post-atc-upsell__bogo-offer')?.dataset.bogoOffer);
      }

      onChange(event) {
        if (!event.target.matches('input[type="radio"]')) return;

        // Switcher changes bubble up to the document for Elevate tracking (assets/combined-listing-popup.js).
        if (event.target.matches('[data-bogo-switch]')) {
          this.showOffer(event.target.value);
          return;
        }

        event.stopPropagation();
        this.update();
      }

      showOffer(handle) {
        this.querySelectorAll('[data-bogo-offer]').forEach((element) => {
          element.hidden = element.dataset.bogoOffer !== handle;
        });

        this.offer = this.querySelector(`.post-atc-upsell__bogo-offer[data-bogo-offer="${handle}"]`);
        if (this.offer) this.update();
      }

      getOfferData() {
        if (!this.offer.bogoData) {
          this.offer.bogoData = JSON.parse(this.offer.querySelector('[data-bogo-offer-data]')?.textContent || '{}');
        }

        return this.offer.bogoData;
      }

      getSelectedVariantId(role) {
        return this.offer.querySelector(`[data-bogo-role="${role}"] input:checked`)?.dataset.variantId;
      }

      update() {
        const data = this.getOfferData();
        const paidId = this.getSelectedVariantId('paid');
        const freeId = this.getSelectedVariantId('free');
        const paid = data.variants?.[paidId];
        const free = data.variants?.[freeId];
        const pairKey = `${paidId}-${freeId}`;
        const pair = data.pairs?.[pairKey];

        this.offer.querySelectorAll('[data-bogo-role]').forEach((fieldset) => {
          const variant = data.variants?.[this.getSelectedVariantId(fieldset.dataset.bogoRole)];
          const selectedValue = fieldset.querySelector('[data-bogo-selected-value]');
          if (variant && selectedValue) selectedValue.textContent = variant.title;
        });

        if (paid) {
          this.setText('[data-bogo-paid-price]', paid.price);
          this.setText('[data-bogo-total]', paid.price);
        }
        if (free) this.setText('[data-bogo-free-price]', free.price);
        if (pair) {
          this.setText('[data-bogo-compare]', pair.compare);
          this.setText('[data-bogo-save]', pair.save);
        }

        this.updateAcceptButton(data, paid, free, paidId, freeId, pairKey);
      }

      updateAcceptButton(data, paid, free, paidId, freeId, pairKey) {
        if (!this.acceptButton) return;

        const label = this.acceptButton.querySelector('[data-bogo-accept-label]');
        const price = this.acceptButton.querySelector('[data-atc-popup-accept-price]');
        if (label && data.acceptLabel) label.textContent = data.acceptLabel;
        if (price && paid) price.textContent = paid.price;

        this.acceptButton.dataset.atcPopupMessage = data.message || '';
        this.acceptButton.dataset.atcPopupItems = JSON.stringify([
          { id: paidId, properties: { _discountMixedBOGOGeneral: paidId, _mixedBOGOPair: pairKey } },
          { id: freeId, properties: { _discountMixedBOGO: 'true', _mixedBOGOPair: pairKey } },
        ]);

        const isAvailable = Boolean(paid?.available && free?.available);
        this.acceptButton.disabled = !isAvailable;
        this.acceptButton.toggleAttribute('aria-disabled', !isAvailable);
      }

      setText(selector, text) {
        const element = this.offer.querySelector(selector);
        if (element) element.textContent = text;
      }
    }
  );
}
